/**
 * GroupSufficiencyCard — V9 proposed group card.
 *
 * Shows a group with its identity (e.g. "Acme Corp"), how many files were
 * uploaded vs required, and an "Upload more" CTA when files are missing.
 *
 * Matches the amber-bordered group card in the V9 mockup (node krhUA).
 */
import React, { JSX, useCallback, useEffect, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import RestoreIcon from '@mui/icons-material/Restore';
import type { SummaryGroup, ExtractionMatch } from 'src/types/aiFileUploadV2';
import { formatMatchValue } from '../utils/formatMatchValue';
import { ConflictResolutionRow } from './ConflictResolutionRow';

// ─── FilledInRow ──────────────────────────────────────────────────────────────

function FilledInRow({ match }: { match: ExtractionMatch }): JSX.Element {
  const theme = useTheme();
  const raw =
    match.user_edited_value ??
    match.proposed_answer_text ??
    match.extracted_value;
  const value = formatMatchValue(
    raw ?? '',
    match.value_type ?? match.qit?.question_type
  );
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 1.5,
        py: 0.5,
        // Prevent any inner content from forcing the row wider than its
        // container (mobile widths can be very narrow).
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          fontSize: 13,
          color: theme.palette.text.secondary,
          // Allow the label to wrap; keep at least ~45% of the row.
          flex: '1 1 45%',
          minWidth: 0,
          overflowWrap: 'anywhere',
        }}
      >
        {match.question_name}
      </Typography>
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 600,
          color: theme.palette.text.primary,
          textAlign: 'right',
          // Hold the value's natural width — short values (dates, currency)
          // shouldn't wrap mid-token. Truly long values (full addresses)
          // still wrap at whitespace via break-word, but never mid-number.
          flex: '0 0 auto',
          minWidth: 0,
          maxWidth: '60%',
          overflowWrap: 'break-word',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

// ─── EditableMatchRow ─────────────────────────────────────────────────────────
//
// Per-paystub matches are auto-accepted by default. When `showActions` is
// enabled (currently disabled — group_answers don't expose actions yet, so
// per-file rows match that for visual consistency), the user can:
//   - click ✎ to edit the value (saves with user_confirmed=true)
//   - click ✕ to exclude this match from Apply All (sets user_excluded=true)
//   - click ↺ on an excluded row to restore it (sets user_excluded=false)
//
// Excluded rows are dimmed but kept in the list so the user can reverse their
// decision. The backend cascade endpoint already propagates these decisions
// to descendant matches when applicable.

interface EditableMatchRowProps {
  match: ExtractionMatch;
  onPatch: (
    fileId: number,
    matchId: number,
    patch: { value?: string; user_confirmed?: boolean; user_excluded?: boolean }
  ) => Promise<ExtractionMatch | null>;
  /**
   * When true, render the inline edit (✎) and exclude (✕) icon buttons.
   * Defaults to false so per-file rows look like read-only group_answers
   * until those gain matching affordances.
   */
  showActions?: boolean;
}

function EditableMatchRow({
  match,
  onPatch,
  showActions = false,
}: EditableMatchRowProps): JSX.Element {
  const theme = useTheme();
  const [excluded, setExcluded] = useState(!!match.user_excluded);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(
    match.user_edited_value ??
      match.proposed_answer_text ??
      match.extracted_value ??
      ''
  );
  const [busy, setBusy] = useState(false);

  const displayRaw =
    match.user_edited_value ??
    match.proposed_answer_text ??
    match.extracted_value;
  const displayValue = formatMatchValue(
    displayRaw ?? '',
    match.value_type ?? match.qit?.question_type
  );

  const handleExclude = useCallback(async () => {
    setBusy(true);
    const updated = await onPatch(match.renter_file_id, match.id, {
      user_excluded: true,
    });
    setBusy(false);
    if (updated) {
      setExcluded(true);
    }
  }, [match.id, match.renter_file_id, onPatch]);

  const handleRestore = useCallback(async () => {
    setBusy(true);
    const updated = await onPatch(match.renter_file_id, match.id, {
      user_excluded: false,
    });
    setBusy(false);
    if (updated) {
      setExcluded(false);
    }
  }, [match.id, match.renter_file_id, onPatch]);

  const handleSave = useCallback(async () => {
    setBusy(true);
    const updated = await onPatch(match.renter_file_id, match.id, {
      value: editValue,
      user_confirmed: true,
    });
    setBusy(false);
    if (updated) {
      setEditing(false);
    }
  }, [match.id, match.renter_file_id, editValue, onPatch]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 1,
        py: 0.5,
        minWidth: 0,
        width: '100%',
        opacity: excluded ? 0.45 : 1,
      }}
    >
      <Typography
        sx={{
          fontSize: 13,
          color: theme.palette.text.secondary,
          // Label grows to take all available width; the value column
          // (dates / currency / short text) only takes what it needs.
          // This keeps long question names like "Pay Period Start Date
          // for Standard Employment Paystub" on as few lines as possible.
          flex: '1 1 auto',
          minWidth: 0,
          overflowWrap: 'anywhere',
        }}
      >
        {match.question_name}
      </Typography>

      {editing ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            flex: '1 1 50%',
            minWidth: 0,
          }}
        >
          <input
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            style={{
              width: '100%',
              minWidth: 0,
              fontSize: 13,
              fontWeight: 600,
              border: 'none',
              borderBottom: `2px solid ${theme.palette.primary.main}`,
              background: 'transparent',
              outline: 'none',
              padding: '2px 4px',
              textAlign: 'right',
            }}
            autoFocus
          />
          <Button
            size="small"
            variant="contained"
            disabled={busy}
            onClick={handleSave}
            sx={{ fontSize: 11, py: 0.25, px: 1, minWidth: 0 }}
          >
            {busy ? (
              <CircularProgress size={10} sx={{ color: 'inherit' }} />
            ) : (
              'Save'
            )}
          </Button>
          <Button
            size="small"
            variant="text"
            disabled={busy}
            onClick={() => setEditing(false)}
            sx={{ fontSize: 11, py: 0.25, px: 0.5, minWidth: 0 }}
          >
            Cancel
          </Button>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 0.25,
            // Hold natural width — short values (dates, currency) shouldn't
            // get squeezed into 1-character columns and wrap mid-number.
            flex: '0 0 auto',
            minWidth: 0,
            maxWidth: '60%',
            justifyContent: 'flex-end',
          }}
        >
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: theme.palette.text.primary,
              textAlign: 'right',
              textDecoration: excluded ? 'line-through' : 'none',
              flex: '0 1 auto',
              minWidth: 0,
              // break-word respects whitespace, so addresses still wrap
              // sensibly while dates / currency strings stay intact.
              overflowWrap: 'break-word',
            }}
            title={displayValue}
          >
            {displayValue}
          </Typography>
          {showActions &&
            (excluded ? (
              <IconButton
                size="small"
                disabled={busy}
                onClick={handleRestore}
                aria-label="Restore"
                sx={{ p: 0.25, flex: '0 0 auto' }}
              >
                {busy ? (
                  <CircularProgress size={12} />
                ) : (
                  <RestoreIcon fontSize="small" />
                )}
              </IconButton>
            ) : (
              <>
                <IconButton
                  size="small"
                  disabled={busy}
                  onClick={() => setEditing(true)}
                  aria-label="Edit"
                  sx={{ p: 0.25, flex: '0 0 auto' }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={busy}
                  onClick={handleExclude}
                  aria-label="Exclude"
                  sx={{ p: 0.25, flex: '0 0 auto' }}
                >
                  {busy ? (
                    <CircularProgress size={12} />
                  ) : (
                    <CloseIcon fontSize="small" />
                  )}
                </IconButton>
              </>
            ))}
        </Box>
      )}
    </Box>
  );
}

// ─── FileAnswersAccordion ─────────────────────────────────────────────────────
//
// Per-file children of a file-match (e.g. "Pay Period Start Date" extracted
// from a single paystub). Rendered inside GroupSufficiencyCard, one accordion
// per file in the group, expanded by default so users see the data without
// extra clicks.
//
// Title shape (mobile-friendly): bold "File #<ordinal>" with the actual
// filename below in a smaller, secondary caption. Filenames in this product
// are usually long and machine-generated, so the ordinal is the primary
// identifier; the filename is supporting context.

interface FileAnswersAccordionProps {
  ordinal: number;
  fileId: number;
  /** Category display name, e.g. "Pay Stub". Preferred title label. */
  categoryDisplay: string | null;
  /** Sub-type display name, fallback when category is unavailable. */
  subTypeDisplay: string | null;
  filename: string | null;
  matches: ExtractionMatch[];
  onPatch: EditableMatchRowProps['onPatch'];
}

function FileAnswersAccordion({
  ordinal,
  fileId,
  categoryDisplay,
  subTypeDisplay,
  filename,
  matches,
  onPatch,
}: FileAnswersAccordionProps): JSX.Element {
  const theme = useTheme();
  // Use category ("Pay Stub") over sub-type ("Standard Employment") — the
  // category is what the renter recognizes as the document type they uploaded.
  const primary = `${categoryDisplay ?? subTypeDisplay ?? 'File'} #${ordinal}`;
  const captionTitle = filename ?? `id=${fileId}`;

  // When there are no extracted children, render a non-interactive row —
  // no expand icon, no collapsible body. Nothing to show means nothing to open.
  if (matches.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          py: 0.75,
          borderBottom: `1px solid ${theme.palette.divider}`,
          '&:last-of-type': { borderBottom: 'none' },
        }}
      >
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color: theme.palette.text.primary,
            lineHeight: 1.2,
          }}
        >
          {primary}
        </Typography>
        <Typography
          sx={{
            fontSize: 11,
            color: theme.palette.text.secondary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={captionTitle}
        >
          {captionTitle}
        </Typography>
      </Box>
    );
  }

  return (
    <Accordion
      defaultExpanded
      disableGutters
      elevation={0}
      square
      sx={{
        // No border / no background / no shadow — the accordion lives
        // inside a bordered section and should not look like a nested
        // card. MUI's default Accordion is built on Paper, which applies
        // its own box-shadow even at elevation={0} in some themes; we
        // override at every level (root + summary + details) so the row
        // ends up visually flat against the surrounding bordered section.
        bgcolor: 'transparent',
        backgroundColor: 'transparent',
        boxShadow: 'none !important',
        borderRadius: 0,
        // Containment: the accordion must never extend past its parent's
        // content box, even when its filename header is very long.
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        // A subtle bottom rule separates one file-row from the next.
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:last-of-type': { borderBottom: 'none' },
        '&:before': { display: 'none' },
        // Defensive overrides for nested Paper/AccordionDetails surfaces
        // that the theme may give a paper background, shadow, or rounded
        // corners (the latter is what makes the bottom of an expanded
        // accordion look "domed" against the section's left border).
        '& .MuiAccordionSummary-root, & .MuiAccordionDetails-root, & .MuiCollapse-root':
          {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            borderRadius: 0,
          },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon fontSize="small" />}
        // MUI's default summary lays out [content] [expandIcon] with the
        // content area as a flex row whose children can overflow unless
        // we explicitly allow them to shrink (min-width: 0).
        sx={{
          minWidth: 0,
          // Zero horizontal padding — the surrounding bordered section
          // already provides the visual inset, and on mobile every pixel
          // of row width matters.
          px: 0,
          py: 0.5,
          '& .MuiAccordionSummary-content': {
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            minWidth: 0,
            my: 0.5,
          },
          // The default expand icon sits inside its own padded button;
          // tighten so it doesn't add visual width on the right.
          '& .MuiAccordionSummary-expandIconWrapper': {
            ml: 0.5,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: '1 1 auto',
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: theme.palette.text.primary,
              lineHeight: 1.2,
            }}
          >
            {primary}
          </Typography>
          <Typography
            sx={{
              fontSize: 11,
              color: theme.palette.text.secondary,
              // Filenames are usually long & machine-generated; ellipsis
              // them in a single line so the row keeps a predictable
              // height across mobile widths.
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
            title={captionTitle}
          >
            {captionTitle}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0.5, px: 0, pb: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            width: '100%',
            // Match the group-root zebra: every other row gets the
            // subtlest theme tint so the eye can scan rows without
            // counting wrap-lines.
            '& > *:nth-of-type(even)': {
              backgroundColor: theme.palette.action.hover,
            },
            '& > *': {
              px: 0.5,
              borderRadius: 0.5,
            },
          }}
        >
          {matches.map((m) => (
            <EditableMatchRow key={m.id} match={m} onPatch={onPatch} />
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

// ─── GroupSufficiencyCard ─────────────────────────────────────────────────────

interface GroupSufficiencyCardProps {
  group: SummaryGroup;
  /** When provided, overrides local open state (used by expand/collapse all). */
  forceOpen?: boolean;
  onPatchMatch?: EditableMatchRowProps['onPatch'];
  onUploadMore: () => void;
}

export function GroupSufficiencyCard({
  group,
  forceOpen,
  onPatchMatch,
  onUploadMore,
}: GroupSufficiencyCardProps): JSX.Element {
  const theme = useTheme();
  const [open, setOpen] = useState(true);

  // Sync with parent's expand/collapse all when forceOpen changes.
  useEffect(() => {
    if (forceOpen !== undefined) {
      setOpen(forceOpen);
    }
  }, [forceOpen]);

  const {
    needs_more_files,
    files_uploaded,
    files_required,
    identity_display,
    group_label,
    group_answers,
    file_answers,
    files,
    fuzzy_target_group_id,
  } = group;

  // Header uses the server-computed group_label (e.g. "Standard Employment #1"
  // or the bare question_group_name for SINGLE groups). Falls back to group_key
  // if somehow missing (shouldn't happen in production).
  const headerText = group_label ?? group.group_key;

  // File-matches (is_file_match=true) arrive under `group_answers` because
  // they are structurally `parent_match=NULL`. Each one represents a
  // RenterFile fulfilling the file-QIT for this proposed group. We surface
  // them as the per-file accordions below (one per file) and use any one of
  // them to derive the file-QIT label that introduces the section.
  const fileMatches = group_answers.filter((m) => m.is_file_match);
  const visibleGroupAnswers = group_answers.filter((m) => !m.is_file_match);

  // The file-QIT's own question text — e.g. "Please upload your most recent
  // pay stubs for this Standard Employment". Used as the section heading
  // above the per-file accordions so the user knows what those files
  // collectively answer. All file-matches in a group share the same QIT,
  // so we can take it from any one.
  const fileQitLabel =
    fileMatches.length > 0 ? fileMatches[0].question_name : null;

  // Per-file accordions: built from group.files[] which comes from the
  // server with ordinals + sub_type_display pre-computed.
  // Fall back to deriving from file_answers / file-matches for SINGLE groups
  // (which have files=[] but might have file_answer children in edge cases).
  const fileEntries =
    files && files.length > 0
      ? files.map((f) => ({
          ordinal: f.ordinal,
          fileId: f.file_id,
          // Prefer category_display ("Pay Stub") over sub_type_display
          // ("Standard Employment") — the category is what the renter
          // understands they uploaded; the sub-type is the classification nuance.
          categoryDisplay: f.category_display,
          subTypeDisplay: f.sub_type_display,
          filename: f.filename,
          matches: (file_answers[f.file_id] ?? []).filter(
            (m) => !m.is_file_match
          ),
        }))
      : (() => {
          const fileIdSet = new Set<number>();
          for (const fid of Object.keys(file_answers)) {
            fileIdSet.add(Number(fid));
          }
          for (const m of fileMatches) {
            if (m.renter_file_id != null) {
              fileIdSet.add(m.renter_file_id);
            }
          }
          return Array.from(fileIdSet)
            .sort((a, b) => a - b)
            .map((fileId, idx) => ({
              ordinal: idx + 1,
              fileId,
              categoryDisplay: null as string | null,
              subTypeDisplay: null as string | null,
              filename: null as string | null,
              matches: (file_answers[fileId] ?? []).filter(
                (m) => !m.is_file_match
              ),
            }));
        })();

  // Map renter_file_id → filename for source attribution in ConflictResolutionRow.
  const fileNamesById: Record<number, string> = {};
  for (const f of fileEntries) {
    if (f.filename != null) {
      fileNamesById[f.fileId] = f.filename;
    }
  }

  // Bucket visibleGroupAnswers by qit_id.
  // - Size 1 → regular FilledInRow (no conflict).
  // - Size >1 → all at the same max confidence (tie) → ConflictResolutionRow.
  // Matches with no qit (qit: null) are placed in their own single-entry
  // bucket keyed by match.id so they still render as FilledInRow.
  const qitBuckets = new Map<number, ExtractionMatch[]>();
  for (const m of visibleGroupAnswers) {
    const bucketId = m.qit?.id ?? -m.id; // negative id avoids collision with real qit ids
    const bucket = qitBuckets.get(bucketId) ?? [];
    bucket.push(m);
    qitBuckets.set(bucketId, bucket);
  }

  // A multi-group has an unresolved conflict when any qit bucket has >1
  // entry with distinct values and no confirmed winner.
  const hasAnyConflict =
    group.kind === 'multi' &&
    Array.from(qitBuckets.values()).some((bucket) => {
      if (bucket.length <= 1) {
        return false;
      }
      const distinctValues = new Set(
        bucket.map((m) =>
          (m.proposed_answer_text ?? m.extracted_value ?? '').trim()
        )
      );
      if (distinctValues.size <= 1) {
        return false;
      }
      return !bucket.some((m) => m.user_confirmed && !m.user_excluded);
    });

  // Border priority (multi-group only):
  //   unresolved conflicts → amber
  //   fuzzy merge (updating existing group) → blue
  //   no conflicts         → green
  //   single group / needs_more_files / default → divider
  const borderColor =
    group.kind === 'multi'
      ? hasAnyConflict
        ? theme.palette.warning.main
        : fuzzy_target_group_id != null
        ? theme.palette.info.main
        : theme.palette.success.main
      : theme.palette.divider;

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: `1px solid ${borderColor}`,
        backgroundColor: theme.palette.background.paper,
        boxShadow: '0 2px 12px rgba(26,25,24,0.03)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        component="button"
        onClick={() => setOpen((o) => !o)}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Typography
            sx={{
              fontSize: 15,
              fontWeight: 600,
              color: theme.palette.text.primary,
            }}
          >
            {headerText}
          </Typography>
          {identity_display && (
            <Typography
              sx={{ fontSize: 12, color: theme.palette.text.secondary }}
            >
              {identity_display}
            </Typography>
          )}
        </Box>
        {open ? (
          <ExpandLessIcon
            sx={{ color: theme.palette.text.secondary, fontSize: 18 }}
          />
        ) : (
          <ExpandMoreIcon
            sx={{ color: theme.palette.text.secondary, fontSize: 18 }}
          />
        )}
      </Box>

      <Collapse in={open}>
        <Box
          sx={{
            px: 2,
            pb: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            // Keep all descendants within the card's content box. Long
            // filenames or values must wrap / ellipsis instead of pushing
            // the layout sideways.
            minWidth: 0,
          }}
        >
          {/* Fuzzy merge info */}
          {fuzzy_target_group_id != null && (
            <Alert severity="info" sx={{ fontSize: 13, py: 0 }}>
              We&rsquo;ll update your existing{' '}
              {group.question_group_name?.toLowerCase() ??
                group.group_label ??
                'entry'}{' '}
              with the information extracted from{' '}
              {files.length === 1 ? 'this document' : 'these documents'}.
            </Alert>
          )}
          {/* Missing files CTA */}
          {needs_more_files && (
            <Box
              sx={{
                bgcolor: theme.palette.warning.main + '22',
                borderRadius: 1.5,
                p: 1.5,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  color: theme.palette.text.primary,
                  lineHeight: 1.5,
                }}
              >
                {fileQitLabel ? (
                  <>
                    <strong>{fileQitLabel}</strong> requires a total of{' '}
                    <strong>{files_required}</strong>{' '}
                    {files_required === 1 ? 'file' : 'files'} and you have
                    provided <strong>{files_uploaded}</strong>. Please upload
                    the remaining {files_required - files_uploaded}.
                  </>
                ) : (
                  <>
                    This question requires a total of{' '}
                    <strong>{files_required}</strong>{' '}
                    {files_required === 1 ? 'file' : 'files'} and you have
                    provided <strong>{files_uploaded}</strong>. Please upload
                    the remaining {files_required - files_uploaded}.
                  </>
                )}
              </Typography>

              <Button
                variant="contained"
                size="small"
                startIcon={<UploadFileIcon />}
                onClick={onUploadMore}
                sx={{
                  bgcolor: theme.palette.warning.main,
                  color: theme.palette.warning.contrastText,
                  fontSize: 13,
                  fontWeight: 600,
                  borderRadius: 1.5,
                  alignSelf: 'stretch',
                  '&:hover': { bgcolor: theme.palette.warning.dark },
                }}
                disableElevation
                data-testid="upload-more-btn"
              >
                <span>Upload more files</span>
              </Button>
            </Box>
          )}

          {/* Group-root answers (employer name, address, etc.) — these
              live at the proposed-group level. Buckets with one match
              render as a regular FilledInRow. Buckets with >1 match are
              exact confidence ties shipped by the backend — they render
              as a ConflictResolutionRow so the user can pick a winner.
              File-matches are stripped above since each one is rendered
              as its own accordion below. */}
          {qitBuckets.size > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                // No gap between rows — the zebra striping below provides
                // visual separation, and gaps would interrupt the bands.
                minWidth: 0,
                width: '100%',
                // Subtle zebra striping for readability. action.hover is
                // the lightest theme tint MUI ships (rgba black 0.04 in
                // light mode); the eye barely registers it but rows stop
                // running together.
                '& > *:nth-of-type(even)': {
                  backgroundColor: theme.palette.action.hover,
                },
                // Tiny horizontal inset on the band so it doesn't bleed
                // edge-to-edge against neighbouring borders.
                '& > *': {
                  px: 0.5,
                  borderRadius: 0.5,
                },
              }}
            >
              {Array.from(qitBuckets.entries()).map(([qitId, bucket]) => {
                if (bucket.length === 1) {
                  return <FilledInRow key={qitId} match={bucket[0]} />;
                }
                // Multiple candidates — only show the conflict picker when
                // values genuinely differ (strip whitespace, preserve case).
                const distinctValues = new Set(
                  bucket.map((m) =>
                    (m.proposed_answer_text ?? m.extracted_value ?? '').trim()
                  )
                );
                if (distinctValues.size <= 1) {
                  // All identical — treat as a single answer (no conflict).
                  return <FilledInRow key={qitId} match={bucket[0]} />;
                }
                return (
                  <ConflictResolutionRow
                    key={qitId}
                    questionName={bucket[0].question_name}
                    candidates={bucket}
                    fileNamesById={fileNamesById}
                    onPatch={onPatchMatch ?? (async () => null)}
                  />
                );
              })}
            </Box>
          )}

          {/* Per-file accordions — one per RenterFile in this group. Each
              represents a single file-match plus its children (e.g. per-
              paystub Pay Period dates, Gross Pay). Expanded by default so
              the user sees their data without an extra click; each row is
              individually editable / excludable via onPatchMatch.

              The section is introduced by the file-QIT's question text
              (e.g. "Please upload your most recent pay stubs …") so the
              user understands these files are answering THAT question.
              The label sits at the same level as every other question
              label (no left border) — only the accordions container
              below gets the left border so the per-file rows read as
              belonging to that file question. */}
          {fileEntries.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                minWidth: 0,
                width: '100%',
              }}
            >
              {fileQitLabel && (
                <Typography
                  sx={{
                    // Match the regular question label style used by
                    // FilledInRow so the file question reads like every
                    // other question in the card.
                    fontSize: 13,
                    color: theme.palette.text.secondary,
                    py: 0.5,
                    overflowWrap: 'anywhere',
                  }}
                >
                  {fileQitLabel}
                </Typography>
              )}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  minWidth: 0,
                  width: '100%',
                  // Tight left padding: the border itself signals the
                  // grouping; we don't need a wide gutter that steals
                  // horizontal space from already-narrow mobile rows.
                  pl: 0.75,
                  borderLeft: `2px solid ${theme.palette.divider}`,
                }}
              >
                {fileEntries.map(
                  ({
                    ordinal: fileOrdinal,
                    fileId,
                    categoryDisplay,
                    subTypeDisplay,
                    filename,
                    matches,
                  }) => (
                    <FileAnswersAccordion
                      key={fileId}
                      ordinal={fileOrdinal}
                      fileId={fileId}
                      categoryDisplay={categoryDisplay}
                      subTypeDisplay={subTypeDisplay}
                      filename={filename}
                      matches={matches}
                      onPatch={onPatchMatch ?? (async () => null)}
                    />
                  )
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

export default GroupSufficiencyCard;
